/**
 * Known places on the atlas (HNR-G10). A pocket map is never an overland node or
 * road: once THIS character has discovered it, it appears only as a small
 * "known place" note in its parent place's details. Undiscovered pocket maps
 * project to nothing at all (no name, no count, no marker).
 *
 * Pure and separate from buildWorldMap on purpose: the portal graph, places and
 * roads are identical whether or not anything is known. Discoverability hides
 * presentation only; it never deletes the source connection.
 */
import { getPocketParent } from '../../../../shared/logic/pocketMaps';
import { normalizeKnownPlaces } from '../../../../shared/logic/knownPlaces';
import type { WorldMapLevelLookup, WorldMapModel } from './worldMapModel';

export interface WorldMapKnownPlace {
  id: string;
  name: string;
}

/** Known pocket places keyed by the parent place id that shows them. */
export type WorldMapKnownPlaces = Readonly<Record<string, readonly WorldMapKnownPlace[]>>;

export function projectKnownPlaces(
  model: Pick<WorldMapModel, 'places'>,
  knownPlaceIds: readonly string[] | null | undefined,
  getLevel: WorldMapLevelLookup,
): WorldMapKnownPlaces {
  const drawn = new Set(model.places.map(place => place.id));
  const byParent: Record<string, WorldMapKnownPlace[]> = {};
  for (const id of normalizeKnownPlaces(knownPlaceIds)) {
    const parentId = getPocketParent(id);
    if (!parentId || !drawn.has(parentId)) continue;
    (byParent[parentId] ??= []).push({ id, name: getLevel(id)?.name?.trim() || id });
  }
  return byParent;
}
